/**
 * One tile's chart.
 *
 * Follows the precedent `render/render-grid.ts` set and explains in its own
 * header: it does **not** reuse `renderLine` / `renderScatter` / `renderHistogram`,
 * because those write through the single `runtime.instance` / `runtime.rootEl`
 * pair that the tooltip, drag-zoom, overlay and image-export paths all read.
 * A dashboard has many charts alive at once, so it keeps its own instances and
 * shares only the pure helpers - which is also what keeps a tile from disturbing
 * whatever Explore has drawn.
 *
 * **Drag-zoom is per chart and view-only**, as Explore's has been since it
 * stopped re-querying (`plot/zoom.ts`): a host that asks for it (`zoom` below)
 * gets uPlot's x-drag, double-click to reset, and a callback with the window,
 * and nothing is fetched. What a tile still does not get: leg and changeover
 * overlays (cached per-draw on `runtime`) and Explore's image export (composes
 * the single chart plus the DOM readouts around it). Those come back with the
 * `PlotInstance` refactor described in `docs/plans/dashboards.md`.
 *
 * **Map tiles are real maps**, not pictures of one: `MapView` in
 * `plot/render/render-map.ts` is per-host for exactly this reason, so a tile
 * mounts its own Leaflet map and draws from the tile's own resolved state. The
 * wheel is left to the page (a dashboard scrolls; a map that swallowed the
 * wheel would trap it), and a continuous colour legend is dropped for the same
 * reason a coloured scatter's is - there is no strip under a tile to put it in.
 * Because a map is not a uPlot instance, every chart a tile draws is returned
 * behind `TileChart`: destroy, resize, and the legend node the tile measures.
 */

import uPlot from 'uplot';
import { nothing, render } from 'lit';
import { tableTemplate } from '../plot/render/render-table.ts';
import { buildSeriesColorer, seriesColorAt } from '../plot/plot-constants.ts';
import { themeColors } from '../plot/render/chart-dom.ts';
import { withAlpha } from '../plot/render/format.ts';
import { buildHeatmapOpts } from '../plot/render/render-heatmap.ts';
import { buildCorrelationOpts } from '../plot/render/render-correlation.ts';
import { buildPieOpts } from '../plot/render/render-pie.ts';
import { buildBoxOpts } from '../plot/render/render-box.ts';
import { buildNumberOpts } from '../plot/render/render-number.ts';
import { buildGaugeOpts } from '../plot/render/render-gauge.ts';
import { buildSankeyOpts } from '../plot/render/render-sankey.ts';
import {
  buildColorBuckets,
  colorPlanFor,
  drawBucketedScatterSeries,
  drawColoredLineSegments,
  planColors,
  pointBuckets,
} from '../plot/render/color-plan.ts';
import { drawColorLegendBox } from '../plot/render/overlays.ts';
import { updateTooltip } from '../plot/render/tooltip.ts';
import {
  applyAxisBounds,
  applyHoverFocus,
  buildLegendOpts,
  applyChartFontVar,
  buildXAxis,
  buildYAxis,
  computeGapThreshold,
  dedupeByX,
  injectGaps,
  bridgeOwnGaps,
  yAxisTitle,
  utcTzDate,
} from '../plot/render/uplot-helpers.ts';
import { createMapView, type MapView } from '../plot/render/render-map.ts';
import type { PlotState } from '../../state/plot-state.ts';
import type {
  AnyPlotResponse,
  MapResponse,
  TableResponse,
  PlotResponse,
  HistogramResponse,
} from '../../api/types.ts';

export interface TileSize {
  width: number;
  height: number;
}

/** Where a tile's chart is mounted, and the nodes its tooltip needs. */
export interface TileMount {
  /** The element uPlot takes over. */
  mount: HTMLElement;
  /** The tooltip node, positioned within `container`. */
  tooltip: HTMLElement;
  /** The positioned ancestor the tooltip is placed against. */
  container: HTMLElement;
}

export interface TileRenderOptions {
  /**
   * The dashboard's shared-crosshair key, or null when it is off. Tiles sync
   * per X axis on top of it - every time-series tile together, and XY tiles
   * with the same X column together - because a crosshair carried across to a
   * chart whose X means something else would mark a meaningless point.
   */
  syncKey?: string | null;
  /** With `syncKey`: tiles the crosshair reaches by sync show their tooltip too. */
  sharedTooltip?: boolean;
  /**
   * The last word on a line or scatter chart's options before uPlot is built,
   * for a host that draws something a tile does not - the SFOC viewer's load
   * guides, per-fuel y-bands and dashed fallback curves. Everything else about
   * the chart stays the one Explore draws.
   */
  decorate?: (opts: uPlot.Options) => void;
  /**
   * Drag-zoom on a line or scatter chart: drag across it to narrow the x axis,
   * double-click to put it back. `initial` re-applies a window the viewer had
   * before a redraw; `onChange` hears every zoom and reset, with null meaning
   * "the whole of the data". Absent, the chart does not zoom - the SFOC viewer
   * and Argus draw with this renderer and keep their own interactions.
   */
  zoom?: TileZoomOptions;
  /**
   * A Live tile's axis follows the clock: the window it should show now, or
   * null when it is not following (refresh off, a fixed window). While it
   * returns one, that window - not the data's extent - is what "not zoomed"
   * means: the chart is built on it, double-click and reset return to it, and
   * the host moves it with `followClock`.
   */
  clock?: () => XWindow | null;
}

/** An x window, in the axis's own units (epoch seconds on a time axis). */
export interface XWindow {
  min: number;
  max: number;
}

export interface TileZoomOptions {
  initial: XWindow | null;
  onChange: (win: XWindow | null) => void;
}

/** Plot types a tile can draw. Every type Explore has. */
export function tileSupports(plotType: string): boolean {
  return (
    plotType === 'line' ||
    plotType === 'scatter' ||
    plotType === 'histogram' ||
    plotType === 'heatmap' ||
    plotType === 'correlation' ||
    plotType === 'pie' ||
    plotType === 'box' ||
    plotType === 'number' ||
    plotType === 'gauge' ||
    plotType === 'sankey' ||
    plotType === 'table' ||
    plotType === 'map'
  );
}

/**
 * One drawn chart, whatever drew it. A tile only ever needs to resize it,
 * measure the legend under it and throw it away, so those are the three things
 * a uPlot instance and a Leaflet map are asked to have in common.
 */
export interface TileChart {
  /** uPlot instance, or null for a map - the export path copies them differently. */
  readonly uplot: uPlot | null;
  /**
   * Redraw in place from new data, keeping what the viewer has done to the
   * chart (a map's pan and zoom). False when this chart cannot show that data
   * and the caller has to build a new one - which is every uPlot chart, since
   * rebuilding one is cheap and it is how a tile has always redrawn.
   */
  update(data: AnyPlotResponse, state: PlotState): boolean;
  /** The `.u-legend` node whose height the tile subtracts, when there is one. */
  legendEl(): HTMLElement | null;
  resize(size: TileSize): void;
  destroy(): void;
  /**
   * Zoom to `win`, or back to the whole of the data with null. Only on charts
   * drawn with `zoom`; the chart's own `onChange` hears it like any other zoom.
   */
  setZoom?(win: XWindow | null): void;
}

function uplotChart(u: uPlot, home?: () => XWindow | null): TileChart {
  return {
    uplot: u,
    ...(home !== undefined
      ? {
          setZoom: (win: XWindow | null): void => {
            const to = win ?? home();
            if (to) u.setScale('x', { min: to.min, max: to.max });
          },
        }
      : {}),
    update: () => false,
    legendEl: () => u.root.querySelector<HTMLElement>('.u-legend'),
    resize: (size) => u.setSize(size),
    destroy: () => u.destroy(),
  };
}

function mapChart(view: MapView): TileChart {
  return {
    uplot: null,
    update: (data, state) => {
      if (data.plot_type !== 'map') return false;
      view.render(data as MapResponse, state);
      return true;
    },
    legendEl: () => null,
    // Leaflet reads its own container; the tile's size change only has to be
    // announced. (`MapView` also watches the mount, so this is the belt to
    // that's braces - a tile resize must not wait for an observer frame.)
    resize: () => view.invalidateSize(),
    destroy: () => view.destroy(),
  };
}

/**
 * A table in a tile. Sorting a tile's table is the viewer's own and lasts until
 * the tile redraws; the saved sort is where it starts.
 */
function tableChart(host: HTMLElement, first: TableResponse, firstState: PlotState): TileChart {
  let data = first;
  let state = firstState;
  let sort = state.table_sort;
  const draw = (): void => {
    render(
      tableTemplate(data, { ...state, table_sort: sort }, {
        onSort: (next) => {
          sort = next;
          draw();
        },
        fontSize: state.style.font_size,
      }),
      host,
    );
  };
  draw();
  return {
    uplot: null,
    update: (next, nextState) => {
      if (next.plot_type !== 'table') return false;
      data = next;
      state = nextState;
      draw();
      return true;
    },
    legendEl: () => null,
    resize: () => {},
    destroy: () => {
      render(nothing, host);
      host.remove();
    },
  };
}

/**
 * Draw `data` into `target`, returning the chart so the caller can resize and
 * destroy it. Returns null for a type a tile cannot draw.
 *
 * Async because a map lazy-loads Leaflet; every other type resolves in the same
 * microtask it was called in.
 */
export async function renderTile(
  target: TileMount,
  data: AnyPlotResponse,
  state: PlotState,
  size: TileSize,
  options: TileRenderOptions = {},
): Promise<TileChart | null> {
  const c = themeColors();
  if (data.plot_type === 'table') {
    const host = document.createElement('div');
    host.className = 'data-table-mount';
    target.mount.appendChild(host);
    return tableChart(host, data, state);
  }
  if (data.plot_type === 'map') {
    // Its own div inside the mount, not the mount itself: the mount is the node
    // the tile measures and clears between draws, and Leaflet wants a box it
    // owns outright.
    const host = document.createElement('div');
    target.mount.appendChild(host);
    const view = await createMapView(host, {
      // Pan and zoom are free - a map re-renders from the points it already
      // has and never refetches - but the wheel belongs to the page.
      scrollWheelZoom: false,
      // A dashboard export composites the tile's canvases, and a WebGL canvas
      // is empty by the time it is copied unless it is asked to keep itself.
      preserveDrawingBuffer: true,
      resizeTarget: target.mount,
    });
    view.render(data, state);
    return mapChart(view);
  }
  if (data.plot_type === 'heatmap') {
    const { opts, uData } = buildHeatmapOpts(
      data,
      c,
      size,
      { el: target.tooltip, container: target.container },
      state,
    );
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'correlation') {
    // No drill-in on a tile: a dashboard is somewhere to read, not to navigate
    // the Explore chart from.
    const { opts, uData } = buildCorrelationOpts(
      data,
      c,
      size,
      { el: target.tooltip, container: target.container },
      state,
    );
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'pie') {
    const { opts, uData } = buildPieOpts(
      data,
      c,
      size,
      { el: target.tooltip, container: target.container },
      state,
    );
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'box') {
    const { opts, uData } = buildBoxOpts(
      data,
      c,
      size,
      { el: target.tooltip, container: target.container },
      state,
    );
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'histogram') {
    return uplotChart(renderTileHistogram(target, data, state, size, c));
  }
  if (data.plot_type === 'number') {
    const { opts, uData } = buildNumberOpts(data, c, size, state);
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'gauge') {
    const { opts, uData } = buildGaugeOpts(data, c, size, state);
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'sankey') {
    const { opts, uData } = buildSankeyOpts(data, c, size, { el: target.tooltip, container: target.container }, state);
    return uplotChart(new uPlot(opts, uData, target.mount));
  }
  if (data.plot_type === 'line' || data.plot_type === 'scatter') {
    const { u, extent } = renderTileXY(
      target,
      data,
      state,
      size,
      c,
      options.syncKey ?? null,
      !!options.sharedTooltip,
      options.decorate,
      options.zoom,
      options.clock,
    );
    const home = (): XWindow | null => options.clock?.() ?? extent;
    return uplotChart(u, options.zoom ? home : undefined);
  }
  return null;
}

/* ── line / scatter ─────────────────────────────────────── */

type ThemeColors = ReturnType<typeof themeColors>;

/**
 * A tile's x + per-series values.
 *
 * Line folds repeated x together (uPlot needs increasing x) and breaks the path
 * across long gaps; **scatter does neither**, because there a repeated x is a
 * real measurement - the same bug that once ate the overlay's and the grid's
 * points. See `panelSeriesData` in `render-grid.ts`, which this mirrors.
 */
function tileSeriesData(
  data: PlotResponse,
  connectGaps: boolean,
  /** Per-point values to put through the same transforms - the colour bucket. */
  extra: Array<Array<number | null>> = [],
  /** Live: each line joins its own points across other tables' rows (`bridgeOwnGaps`). */
  bridge = false,
): { x: number[]; series: Array<Array<number | null>>; extra: Array<Array<number | null>> } {
  const xs = data.x.slice();
  const values = [...data.series.map((ser) => ser.values.slice()), ...extra.map((a) => a.slice())];
  const split = (
    result: { x: number[]; series: Array<Array<number | null>> },
  ): { x: number[]; series: Array<Array<number | null>>; extra: Array<Array<number | null>> } => ({
    x: result.x,
    series: result.series.slice(0, data.series.length),
    extra: result.series.slice(data.series.length),
  });
  if (data.plot_type === 'scatter') return split({ x: xs, series: values });
  const sanitized = dedupeByX(xs, values);
  const threshold = connectGaps ? null : computeGapThreshold(sanitized.x);
  const gapped = injectGaps(sanitized.x, sanitized.series, threshold);
  if (bridge && !connectGaps) {
    const n = data.series.length;
    gapped.series.splice(0, n, ...bridgeOwnGaps(gapped.x, gapped.series.slice(0, n)));
  }
  return split(gapped);
}

function renderTileXY(
  target: TileMount,
  data: PlotResponse,
  state: PlotState,
  size: TileSize,
  c: ThemeColors,
  syncKey: string | null,
  sharedTooltip: boolean,
  decorate?: (opts: uPlot.Options) => void,
  zoom?: TileZoomOptions,
  clock?: () => XWindow | null,
): { u: uPlot; extent: XWindow | null } {
  const style = state.style;
  // A tile draws from its own spec, not from what Explore happens to be showing.
  const fontPx = style.font_size;
  const isScatter = data.plot_type === 'scatter';
  const hasRight = data.series.some((ser) => ser.axis === 'right');
  const colorOf = buildSeriesColorer(state, data.series, null);

  /*
   * Colour by a column, exactly as Explore draws it: a scatter colours its
   * points, a line colours its own segments (`render/color-plan.ts`). The tile
   * was already asking for the column - `buildPlotRequest` puts `color_by` in
   * every line and scatter request - and then drew the answer in flat series
   * colours, so a tile saved from a coloured chart quietly lost the third
   * dimension it was saved for.
   */
  const colorScaleName = state.color_scale || 'Viridis';
  const colorPlan =
    data.color_by && data.color_values?.length ? colorPlanFor([{ data }], colorScaleName) : null;
  const scatterBuckets =
    colorPlan && isScatter ? buildColorBuckets(data.color_values!, colorPlan, colorScaleName) : null;
  const perPoint = colorPlan && !isScatter ? pointBuckets(data.color_values!, colorPlan) : null;
  // Only a Live tile has a clock.
  const plotted = tileSeriesData(data, !!style.connect_gaps, perPoint ? [perPoint] : [], !!clock);
  const lineBuckets = perPoint ? plotted.extra[0]! : null;
  const lineColors = colorPlan ? planColors(colorPlan, colorScaleName) : [];
  // A gradient has no room to explain itself on a tile - the strip under the
  // Explore chart is not here - but a handful of named categories does, and the
  // box is the same one the grid panels draw.
  const legendBox =
    colorPlan?.kind === 'discrete' && data.color_by
      ? { title: data.color_by, items: colorPlan.legend }
      : null;

  // On a multi-ship overlay each series is null wherever another ship sampled -
  // structural nulls, not missing data. With `spanGaps` off uPlot breaks the
  // path at every one of them and the chart draws nothing. Same reasoning as
  // `render-line.ts`.
  const multiShip = data.series.some((ser) => ser.ship);
  /** Whether the pointer is over this tile, rather than reaching it by sync. */
  let hovered = false;
  const spanGaps = !!style.connect_gaps || multiShip;

  const seriesOpts: uPlot.Series[] = [{}];
  const uData: uPlot.AlignedData = [plotted.x as number[]];
  /** The data's own x range - what "not zoomed" means. */
  const extent = xExtent(plotted.x as ArrayLike<number | null>);
  /** What "not zoomed" means now: the clock's window while following, else the data's. */
  const home = (): XWindow | null => clock?.() ?? extent;
  /** True while uPlot is being built, when its setScale hooks are not a zoom. */
  let building = true;
  const seriesScales: string[] = ['x'];
  const alpha = Math.max(0, Math.min(1, style.scatter_alpha ?? 0.7));
  data.series.forEach((ser, i) => {
    const color = colorOf(ser, i);
    const pointColor = isScatter ? withAlpha(color, alpha) : color;
    const scale = ser.axis === 'right' && hasRight ? 'yR' : 'y';
    const opt: uPlot.Series = {
      label: ser.name,
      stroke: color,
      width: style.line_width,
      scale,
      spanGaps,
      points: {
        // uPlot's points are one colour per series; a coloured scatter draws
        // its own below and must not be drawn twice.
        show: scatterBuckets ? false : isScatter || !!style.show_points,
        size: isScatter
          ? Math.max(2, style.scatter_point_size)
          : Math.max(4, style.line_width + 3),
        stroke: pointColor,
        fill: pointColor,
      },
    };
    // Scatter never draws a path; a coloured line draws its own, below.
    if (isScatter || lineBuckets) opt.paths = () => null;
    seriesOpts.push(opt);
    seriesScales.push(scale);
    uData.push(plotted.series[i] as Array<number | null>);
  });

  const logCfg = style.log_scale ? { distr: 3 as const, log: 10 as const } : {};
  const scales: Record<string, uPlot.Scale> = {
    x: { time: !!data.x_is_time },
    y: { ...logCfg },
  };
  if (hasRight) scales.yR = { ...logCfg };
  applyAxisBounds(scales, !!data.x_is_time, hasRight, state.axis_bounds);

  const leftLabel = yAxisTitle(data.series, 'left', state.axis_titles.y);
  const rightLabel = yAxisTitle(data.series, 'right', state.axis_titles.yr);
  const axes: uPlot.Axis[] = [
    buildXAxis(c, !!data.x_is_time, data.x_column || (data.x_is_time ? 'Time' : 'x'), fontPx),
    buildYAxis(c, 'y', 'left', leftLabel, fontPx),
  ];
  if (hasRight) axes.push(buildYAxis(c, 'yR', 'right', rightLabel, fontPx));

  const opts: uPlot.Options = {
    ...size,
    scales,
    axes,
    series: seriesOpts,
    legend: buildLegendOpts(seriesOpts.length, style.show_legend),
    // Drag-zoom only for a host that asked for it and shows that the chart is
    // zoomed (the dashboard tile's header does); otherwise a zoomed chart would
    // silently disagree with the date range the toolbar says is showing.
    cursor: {
      x: true,
      y: !isScatter,
      drag: zoom ? { x: true, y: false, setScale: true } : { x: false, y: false },
      ...(syncKey
        ? {
            sync: {
              key: `${syncKey}|${data.x_is_time ? 'time' : `x:${data.x_column ?? ''}`}`,
              setSeries: false,
            },
          }
        : {}),
    },
    hooks: {
      ...(scatterBuckets
        ? {
            drawSeries: [
              (u: uPlot, seriesIndex: number): void => {
                if (seriesIndex < 1 || seriesIndex > data.series.length) return;
                drawBucketedScatterSeries(
                  u,
                  seriesIndex,
                  uData,
                  seriesScales[seriesIndex]!,
                  scatterBuckets,
                  style.scatter_point_size,
                  style.scatter_alpha,
                );
              },
            ],
          }
        : {}),
      draw: [
        (u: uPlot): void => {
          if (lineBuckets) {
            for (let i = 1; i <= data.series.length; i++) {
              drawColoredLineSegments(
                u,
                i,
                uData,
                seriesScales[i]!,
                lineBuckets,
                lineColors,
                style.line_width,
                spanGaps,
              );
            }
          }
          if (legendBox && (scatterBuckets || lineBuckets)) drawColorLegendBox(u, legendBox);
        },
      ],
      setCursor: [
        (u: uPlot): void => {
          // Without the shared tooltip, the other tiles show only the line
          // and the one under the pointer reads out its values. With it, each
          // tile reads out its own values at the synced point, as grid panels do.
          if (syncKey && !hovered && !sharedTooltip) {
            target.tooltip.classList.remove('visible');
            delete target.tooltip.dataset.ttIdx;
            return;
          }
          updateTooltip(u, data, seriesOpts, undefined, {
            el: target.tooltip,
            container: target.container,
          });
        },
      ],
      setScale: [
        (u: uPlot, key: string): void => {
          const at = home();
          if (key !== 'x' || !zoom || building || !at) return;
          const min = u.scales.x?.min;
          const max = u.scales.x?.max;
          if (min == null || max == null || !Number.isFinite(min) || !Number.isFinite(max) || max <= min) return;
          zoom.onChange(sameWindow({ min, max }, at) ? null : { min, max });
        },
      ],
      init: [
        (u: uPlot): void => {
          u.over.addEventListener('mouseenter', () => {
            hovered = true;
          });
          u.over.addEventListener('mouseleave', () => {
            hovered = false;
          });
        },
      ],
    },
  };
  if (data.x_is_time) opts.tzDate = utcTzDate;
  applyHoverFocus(opts, style.focus_hover);
  if (clock) {
    // uPlot's own double-click fits the data; a following chart goes to now.
    opts.cursor = {
      ...opts.cursor,
      bind: {
        ...opts.cursor?.bind,
        dblclick: (self, _targ, handler) => (e) => {
          const to = home();
          if (to) self.setScale('x', { min: to.min, max: to.max });
          else handler(e);
          return null;
        },
      },
    };
  }
  decorate?.(opts);

  const u = new uPlot(opts, uData, target.mount);
  // A window from before a redraw is put back without being reported as a new
  // zoom - the host already has it. A following chart starts on the clock.
  const start = zoom?.initial ?? clock?.() ?? null;
  if (start && extent) u.setScale('x', { min: start.min, max: start.max });
  building = false;
  return { u, extent };
}

/** Whether two windows are the same, within a thousandth of the span. */
function sameWindow(a: XWindow, b: XWindow): boolean {
  const eps = Math.max(1e-9, b.max - b.min) * 0.001;
  return Math.abs(a.min - b.min) < eps && Math.abs(a.max - b.max) < eps;
}

/** The finite extent of the x values, or null when there is nothing to zoom over. */
function xExtent(xs: ArrayLike<number | null>): XWindow | null {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < xs.length; i++) {
    const v = xs[i];
    if (v == null || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min < max ? { min, max } : null;
}

/* ── histogram ──────────────────────────────────────────── */

function renderTileHistogram(
  target: TileMount,
  data: HistogramResponse,
  state: PlotState,
  size: TileSize,
  c: ThemeColors,
): uPlot {
  const edges = data.edges;
  const centers = edges.slice(0, -1).map((e, i) => (e + edges[i + 1]!) / 2);

  const uData: uPlot.AlignedData = [centers];
  const seriesOpts: uPlot.Series[] = [{}];
  const barsPaths = uPlot.paths?.bars ? uPlot.paths.bars({ size: [0.9], align: 0 }) : undefined;
  data.series.forEach((ser, i) => {
    const color = seriesColorAt(state, ser.name, i);
    const opt: uPlot.Series = {
      label: ser.name,
      scale: 'y',
      stroke: color,
      fill: withAlpha(color, data.series.length > 1 ? 0.45 : 0.75),
      width: 1,
      points: { show: false },
    };
    if (barsPaths) opt.paths = barsPaths;
    seriesOpts.push(opt);
    uData.push(ser.counts.map((v) => (v == null ? null : Number(v))));
  });

  const opts: uPlot.Options = {
    ...size,
    scales: { x: { time: false }, y: {} },
    axes: [
      buildXAxis(
        c,
        false,
        data.columns && data.columns.length === 1 ? data.columns[0]! : 'Value',
        state.style.font_size,
      ),
      buildYAxis(c, 'y', 'left', 'Count', state.style.font_size),
    ],
    series: seriesOpts,
    cursor: { x: true, y: false, drag: { x: false, y: false } },
    legend: buildLegendOpts(data.series.length, state.style.show_legend),
    hooks: {
      setCursor: [
        (u: uPlot): void =>
          updateTooltip(u, data, seriesOpts, { isHist: true, edges }, {
            el: target.tooltip,
            container: target.container,
          }),
      ],
    },
  };
  applyHoverFocus(opts, state.style.focus_hover);

  return new uPlot(opts, uData, target.mount);
}
